import React, { useState } from 'react';
import { useLocationServices } from '../hooks/useLocationServices';

const LocationComponent = () => {
    const [watchMode, setWatchMode] = useState(false);
    const [targetLocation, setTargetLocation] = useState({ lat: '', lon: '', radius: 1 });

    const {
        location,
        isLoading,
        error,
        permissionStatus,
        isSupported,
        isWatching,
        status,
        formattedLocation,
        requestLocation,
        startWatching,
        stopWatching,
        clearLocation,
        isWithinRadius,
        calculateDistance
    } = useLocationServices();

    const handleGetLocation = async () => {
        const result = await requestLocation();
        if (!result.success) {
            console.error('Failed to get location:', result.error);
        }
    };

    const handleToggleWatch = () => {
        if (isWatching) {
            stopWatching();
            setWatchMode(false);
        } else {
            startWatching();
            setWatchMode(true);
        }
    };

    const calculateDistanceToTarget = () => {
        if (!location || !targetLocation.lat || !targetLocation.lon) return null;

        return calculateDistance(
            location.latitude,
            location.longitude,
            parseFloat(targetLocation.lat),
            parseFloat(targetLocation.lon)
        );
    };

    const checkProximity = () => {
        if (!targetLocation.lat || !targetLocation.lon) return null;

        return isWithinRadius(
            parseFloat(targetLocation.lat),
            parseFloat(targetLocation.lon),
            parseFloat(targetLocation.radius)
        );
    };

    const getStatusBadgeClass = () => {
        switch (status) {
            case 'available': return 'bg-success';
            case 'loading': return 'bg-warning';
            case 'error': return 'bg-danger';
            case 'denied': return 'bg-danger';
            case 'unsupported': return 'bg-secondary';
            default: return 'bg-secondary';
        }
    };

    return (
        <div className="container mt-4">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Location Services</h5>
                            <span className={`badge ${getStatusBadgeClass()}`}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                        </div>

                        <div className="card-body">
                            {/* Support and Permission Status */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${isSupported ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} me-2`}></i>
                                        <span>Browser Support: {isSupported ? 'Supported' : 'Not Supported'}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${
                                            permissionStatus === 'granted' ? 'bi-check-circle-fill text-success' :
                                                permissionStatus === 'denied' ? 'bi-x-circle-fill text-danger' :
                                                    'bi-question-circle-fill text-warning'
                                        } me-2`}></i>
                                        <span>Permission: {permissionStatus}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div className="btn-group me-2" role="group">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleGetLocation}
                                            disabled={!isSupported || isLoading || permissionStatus === 'denied'}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Getting Location...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-geo-alt me-2"></i>
                                                    Get Current Location
                                                </>
                                            )}
                                        </button>

                                        <button
                                            className={`btn ${isWatching ? 'btn-danger' : 'btn-success'}`}
                                            onClick={handleToggleWatch}
                                            disabled={!isSupported || permissionStatus === 'denied'}
                                        >
                                            <i className={`bi ${isWatching ? 'bi-stop-circle' : 'bi-play-circle'} me-2`}></i>
                                            {isWatching ? 'Stop Watching' : 'Watch Location'}
                                        </button>

                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={clearLocation}
                                            disabled={!location}
                                        >
                                            <i className="bi bi-trash me-2"></i>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                </div>
                            )}

                            {/* Location Information */}
                            {location && (
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="card bg-light">
                                            <div className="card-header">
                                                <h6 className="mb-0">Current Location</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <strong>Coordinates:</strong><br />
                                                        {formattedLocation?.coordinates}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <strong>Accuracy:</strong><br />
                                                        {formattedLocation?.accuracy}
                                                    </div>
                                                </div>
                                                {location.altitude && (
                                                    <div className="row mt-2">
                                                        <div className="col-md-6">
                                                            <strong>Altitude:</strong><br />
                                                            {Math.round(location.altitude)}m
                                                        </div>
                                                        <div className="col-md-6">
                                                            <strong>Speed:</strong><br />
                                                            {location.speed ? `${Math.round(location.speed * 3.6)} km/h` : 'N/A'}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="row mt-2">
                                                    <div className="col-12">
                                                        <strong>Last Updated:</strong><br />
                                                        {formattedLocation?.timestamp}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Distance Calculator */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h6 className="mb-0">Distance Calculator & Proximity Check</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">Target Latitude</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={targetLocation.lat}
                                                            onChange={(e) => setTargetLocation(prev => ({...prev, lat: e.target.value}))}
                                                            placeholder="e.g., 40.7128"
                                                            step="any"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">Target Longitude</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={targetLocation.lon}
                                                            onChange={(e) => setTargetLocation(prev => ({...prev, lon: e.target.value}))}
                                                            placeholder="e.g., -74.0060"
                                                            step="any"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">Radius (km)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={targetLocation.radius}
                                                            onChange={(e) => setTargetLocation(prev => ({...prev, radius: e.target.value}))}
                                                            placeholder="1"
                                                            min="0"
                                                            step="0.1"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {location && targetLocation.lat && targetLocation.lon && (
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="alert alert-info">
                                                            <strong>Distance:</strong><br />
                                                            {calculateDistanceToTarget()?.toFixed(2)} km
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className={`alert ${checkProximity() ? 'alert-success' : 'alert-warning'}`}>
                                                            <strong>Within Radius:</strong><br />
                                                            {checkProximity() ? 'Yes ✓' : 'No ✗'}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationComponent;